import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import SendIcon from '@mui/icons-material/Send';
import DescriptionIcon from '@mui/icons-material/Description';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import BackupIcon from '@mui/icons-material/Backup';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import InsightsIcon from '@mui/icons-material/Insights';
import TaskIcon from '@mui/icons-material/Task';
import EmailIcon from '@mui/icons-material/Email';
import ForumIcon from '@mui/icons-material/Forum';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import ArticleIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventIcon from '@mui/icons-material/Event';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import BadgeIcon from '@mui/icons-material/Badge';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import GroupsIcon from '@mui/icons-material/Groups';
import ApiIcon from '@mui/icons-material/Api';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import RuleIcon from '@mui/icons-material/Rule';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublicIcon from '@mui/icons-material/Public';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import SpeedIcon from '@mui/icons-material/Speed';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import TuneIcon from '@mui/icons-material/Tune';
import PaidIcon from '@mui/icons-material/Paid';
import HubIcon from '@mui/icons-material/Hub';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import authService from '../services/authService';
import ErrorBoundary from './ErrorBoundary';
import IdleWarningDialog from './IdleWarningDialog';
import NotificationBell from '../modules/communication/components/NotificationBell';
import GlobalSearchBar from './GlobalSearchBar';
import TenantDevBanner from './TenantDevBanner';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import CommandPalette from './design-system/CommandPalette';
import ThemeModeToggle from './design-system/ThemeModeToggle';
import { useTrackRecentPage, getRecentPages } from '../hooks/useRecentPages';
import { getFavoritePages, isFavoritePage, toggleFavoritePage } from '../hooks/useFavoritePages';
import pageRegistry from '../navigation/pageRegistry';
import { getVisibleNavItems } from './design-system/RoleAwareMenu';

const drawerWidth = 240;

// RC18.2 Phase 3 — best-effort exact-path lookup against the command palette's page
// registry; dynamic detail routes (e.g. `/candidates/:id`) intentionally fall through to
// `null` (not tracked as a "recent page") since the registry only lists top-level pages.
function resolvePageLabel(pathname) {
  const match = pageRegistry.find((p) => p.path === pathname);
  return match ? match.label : null;
}

const IDLE_TIMEOUT_MS   = 30 * 60 * 1000; // 30 minutes
const IDLE_WARNING_MS   =  2 * 60 * 1000; // warn 2 minutes before expiry

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [idleWarningOpen, setIdleWarningOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [favoriteVersion, setFavoriteVersion] = useState(0);
  const [, forcePermissionsRefresh] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // RC17.6.1 root-cause fix — hasPermission() reads a plain localStorage snapshot taken at
  // login, with no reactivity: a tab left open across a Security Group change (made by an
  // admin, or directly in the database) never picks it up on its own, since nothing ever
  // triggers a re-render. Refreshing once on mount and forcing a re-render once it resolves
  // makes an already-open tab self-heal on next navigation/reload without requiring an
  // explicit logout/login cycle.
  useEffect(() => {
    authService.fetchAndCachePermissions().then(() => forcePermissionsRefresh((v) => v + 1));
  }, []);

  // RC18.2 Phase 3 — feeds the Command Palette's "Recently visited" section.
  useTrackRecentPage(resolvePageLabel);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    authService.logout('manual');
  };

  const handleStaySignedIn = () => {
    setIdleWarningOpen(false);
    // Closing the dialog is itself activity — the hook's reset was already called
    // by the click event listener, so we don't need to call reset() manually.
  };

  const handleIdleExpire = () => {
    authService.logout('idle');
  };

  useIdleTimeout({
    timeout: IDLE_TIMEOUT_MS,
    warningBefore: IDLE_WARNING_MS,
    onWarn: () => setIdleWarningOpen(true),
    onExpire: handleIdleExpire,
  });

  // RC19.x Role-Aware UI — every nav item below declares the permission it needs and
  // `getVisibleNavItems` (one shared filter, see design-system/RoleAwareMenu.js) decides
  // membership; no per-item boolean, no per-item spread. Same items, same permission codes, same
  // paths as before this refactor — this only replaces ~40 hand-written
  // `const canViewX = hasPermission(...)` + `...(canViewX ? [...] : [])` pairs with data.
  // (RC14.0 already eliminated role-name checks entirely; this is centralizing the permission
  // checks that replaced them, not introducing permission-gating for the first time.)
  const dashboardItem = { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' };
  // User Guide is deliberately ungated (no `permission`) and pinned above Dashboard — every
  // signed-in user benefits from it regardless of role, and it should be the first thing anyone
  // reaches for, not buried inside a collapsed group.
  const userGuideItem = { text: 'User Guide', icon: <AutoStoriesIcon />, path: '/user-guide' };

  const workspaceGroups = [
    {
      key: 'candidates',
      label: 'Candidates',
      icon: <PeopleIcon />,
      items: getVisibleNavItems([
        { text: 'Candidates', icon: <PeopleIcon />, path: '/candidates' },
        { text: 'Resumes', icon: <DescriptionIcon />, path: '/resumes' },
      ]),
    },
    {
      key: 'clients-vendors',
      label: 'Clients & Vendors',
      icon: <BusinessIcon />,
      items: getVisibleNavItems([
        { text: 'Clients', icon: <BusinessIcon />, path: '/clients' },
        { text: 'Account Manager Workspace', icon: <PaidIcon />, path: '/account-manager' },
        { text: 'Vendors', icon: <StoreIcon />, path: '/vendors' },
        { text: 'Vendor Analytics', icon: <BarChartIcon />, path: '/vendor-analytics' },
        { text: 'Market Intelligence', icon: <TrendingUpIcon />, path: '/market-intelligence' },
      ]),
    },
    {
      key: 'pipeline',
      label: 'Recruiting Pipeline',
      icon: <WorkIcon />,
      items: getVisibleNavItems([
        { text: 'Jobs', icon: <WorkIcon />, path: '/jobs' },
        { text: 'Submissions', icon: <SendIcon />, path: '/submissions' },
        { text: 'Pipeline', icon: <ViewKanbanIcon />, path: '/pipeline' },
        { text: 'Interviews', icon: <EventIcon />, path: '/interviews' },
        { text: 'Interview Intelligence', icon: <PsychologyIcon />, path: '/interview-intelligence', permission: 'InterviewIntelligence.View' },
        { text: 'Offers', icon: <LocalOfferIcon />, path: '/offers' },
      ]),
    },
    {
      key: 'workforce',
      label: 'Workforce',
      icon: <BadgeIcon />,
      items: getVisibleNavItems([
        { text: 'Placements', icon: <WorkHistoryIcon />, path: '/placements', permission: 'Placement.View' },
        { text: 'Consultants', icon: <BadgeIcon />, path: '/consultants', permission: 'Consultant.View' },
        { text: 'Bench', icon: <EventSeatIcon />, path: '/bench', permission: 'Bench.View' },
      ]),
    },
    {
      key: 'productivity',
      label: 'Productivity',
      icon: <TaskIcon />,
      items: getVisibleNavItems([
        { text: 'My Tasks', icon: <TaskIcon />, path: '/tasks' },
        { text: 'Calendar', icon: <CalendarMonthIcon />, path: '/calendar', permission: 'Calendar.View' },
        { text: 'Email Templates', icon: <EmailIcon />, path: '/email-templates' },
        { text: 'Communication Center', icon: <ForumIcon />, path: '/communications', permission: 'Communication.View' },
        { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
        { text: 'Activity Timeline', icon: <HistoryIcon />, path: '/activity' },
      ]),
    },
    {
      key: 'insights',
      label: 'Insights',
      icon: <AssessmentIcon />,
      items: getVisibleNavItems([
        { text: 'Knowledge Base', icon: <MenuBookIcon />, path: '/knowledge', permission: 'Knowledge.View' },
        { text: 'Reports', icon: <AssessmentIcon />, path: '/reporting', permission: 'Reporting.View' },
        { text: 'Dashboards', icon: <DashboardCustomizeIcon />, path: '/reporting/dashboards', permission: 'Reporting.Dashboard' },
        { text: 'Performance', icon: <LeaderboardIcon />, path: '/performance' },
        { text: 'Executive Dashboard', icon: <InsightsIcon />, path: '/executive', permission: 'Forecast.View' },
        { text: 'My Organization', icon: <CorporateFareIcon />, path: '/customer/portal', permission: 'Customer.View' },
      ]),
    },
    {
      key: 'monitoring',
      label: 'Monitoring',
      icon: <MonitorHeartIcon />,
      items: getVisibleNavItems([
        { text: 'Operations Center', icon: <MonitorHeartIcon />, path: '/operations', permission: 'Operations.View' },
        { text: 'System Logs', icon: <ArticleIcon />, path: '/system-logs', permission: 'Logs.View' },
        { text: 'Backups', icon: <BackupIcon />, path: '/backups', permission: 'Backup.View' },
        { text: 'Integration Center', icon: <HubIcon />, path: '/integrations', permission: 'Integration.View' },
      ]),
    },
  ].filter((group) => group.items.length > 0);

  // RC14.3 — Platform Administration is a distinct nav section from Tenant Administration: it's
  // cross-tenant platform-operator tooling (Platform.*/Tenant.Switch/System.Operate/Tenant.*/
  // FeatureManagement.*), separate from per-tenant administration (User.*/Permission.*/
  // TenantSecurityGroup.*/PermissionProfile.*, which only ever reach the caller's own tenant).
  // The "Tenant Administration" SECTION (not any one item within it) is visible if the caller
  // holds ANY of these codes — preserved exactly from the pre-refactor boolean OR-chain, which
  // included a few codes (Forecast/Operations/Logs/Backup.View) that are also independently used
  // by items in workspaceGroups above; that overlap is original behavior, not new.
  // PR-REL-005 — Identity.View deliberately removed from this trigger list: Identity Center is
  // hidden from V1 navigation entirely (see adminSubItems below and App.js's route comment), so
  // holding only Identity.View must not be the sole reason this whole section appears with
  // nothing in it a caller can actually use.
  const TENANT_ADMIN_TRIGGER_PERMISSIONS = [
    'Forecast.View', 'Operations.View', 'Logs.View', 'Backup.View',
    'User.View', 'Permission.View', 'Security.View',
    'SecurityGroup.View', 'TenantSecurityGroup.View',
    'PermissionProfile.View', 'ServiceAccount.View',
    'PublicApi.View', 'DataImport.View',
  ];
  const isTenantAdmin = TENANT_ADMIN_TRIGGER_PERMISSIONS.some((p) => authService.hasPermission(p));

  // Per RC14.3 Phase 8: the section itself is visible only with Platform.Manage; individual
  // sub-items still each check their own specific permission for defense in depth.
  const isPlatformOperator = authService.hasPermission('Platform.Manage');

  // RC18.7 Phase 2 — a group auto-opens the first time its own active route is visited, so
  // landing on (say) /consultants doesn't require an extra click to see where you are. Never
  // re-collapses a group the user has since opened/closed by hand (dependency is the pathname
  // that triggered the match, not every keystroke of navigation).
  useEffect(() => {
    const activeGroup = workspaceGroups.find((g) => g.items.some((i) => i.path === location.pathname));
    if (activeGroup) {
      setOpenGroups((prev) => (prev[activeGroup.key] ? prev : { ...prev, [activeGroup.key]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  // RC18.7 Phase 2 — Favorites: a star toggle for the current page (AppBar) plus a quick-jump
  // list at the top of the drawer. `useMemo` re-derives from localStorage on every navigation
  // and every favorite toggle (favoriteVersion) rather than trying to keep a separate React
  // state array in sync with the same storage `useRecentPages` already reads/writes.
  const currentPageLabel = resolvePageLabel(location.pathname);
  const isCurrentPageFavorite = useMemo(
    () => (currentPageLabel ? isFavoritePage(location.pathname) : false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, currentPageLabel, favoriteVersion]
  );
  const favoritePages = useMemo(
    () => getFavoritePages(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [favoriteVersion, location.pathname]
  );
  const recentPages = useMemo(
    () => getRecentPages().filter((r) => r.path !== location.pathname).slice(0, 4),
    [location.pathname]
  );
  const handleToggleFavorite = () => {
    if (!currentPageLabel) return;
    toggleFavoritePage(location.pathname, currentPageLabel);
    setFavoriteVersion((v) => v + 1);
  };

  // RC17.6.1 — Security Administration is its own nested section (AEM-inspired): Users,
  // Security Groups, Permission Profiles, Permissions, Effective Permissions, Audit. Roles are
  // never surfaced anywhere in this nav — access is entirely Security Group membership.
  // `Permission Groups` (the older, organizational-only concept) stays a flat Administration
  // item, deliberately not folded into Security — it has no relationship to authorization.
  const securitySubItems = getVisibleNavItems([
    { text: 'Users', icon: <ManageAccountsIcon />, path: '/admin/users', permission: 'User.View' },
    // RC18.6.2 — Security Groups/Permission Profiles are reachable by two different permission
    // tiers: the original Platform-Operator-only codes (global system-group administration,
    // unchanged) OR the new tenant-scoped codes (a tenant Administrator's own groups/templates
    // only). Either holder sees the nav item; SecurityGroupService/PermissionProfileService
    // enforce what they can actually reach once there.
    { text: 'Security Groups', icon: <ShieldIcon />, path: '/admin/security-groups', permission: ['SecurityGroup.View', 'TenantSecurityGroup.View'] },
    { text: 'Permission Profiles', icon: <HistoryEduIcon />, path: '/admin/permission-profiles', permission: 'PermissionProfile.View' },
    { text: 'Permissions', icon: <VpnKeyIcon />, path: '/admin/permissions', permission: 'Permission.View' },
    { text: 'Effective Permissions', icon: <FactCheckIcon />, path: '/admin/security/effective-permissions', permission: 'Security.View' },
    { text: 'Audit', icon: <RuleIcon />, path: '/admin/security', permission: 'Security.View' },
  ]);

  // RC17.6.1 — Security Administration is its own nested section (AEM-inspired) — see
  // securitySubItems above. Roles are never surfaced anywhere in this nav — access is entirely
  // Security Group membership. `Permission Groups` (the older, organizational-only concept) stays
  // a flat Administration item, deliberately not folded into Security — it has no relationship to
  // authorization. Teams/Departments/Locations are intentionally ungated placeholders
  // (`disabled: true`, no `permission`) — "Coming soon," not access-restricted.
  // PR-REL-005 — Identity Center is deliberately absent from this list, not merely permission-
  // gated: the V1 UI Exposure Audit found it presents a complete-looking SSO/SCIM management
  // experience with no live federation behind it (see App.js's route comment for the full
  // reasoning). Unlike Teams/Departments/Locations below, this isn't a "Coming soon" placeholder
  // to show-and-disable — it's fully removed from V1 navigation, matching the audit's own "does
  // not see it" acceptance criterion. Identity.View/Identity.Manage and the page/service
  // implementation are untouched; only this nav entry point is gone.
  const adminSubItems = getVisibleNavItems([
    { text: 'Permission Groups', icon: <GroupsIcon />, path: '/admin/permission-groups', permission: 'Permission.Manage' },
    { text: 'Service Accounts', icon: <SmartToyIcon />, path: '/admin/service-accounts', permission: 'ServiceAccount.View' },
    { text: 'Developer Portal', icon: <ApiIcon />, path: '/admin/developer-portal', permission: 'PublicApi.View' },
    { text: 'Data Import', icon: <UploadFileIcon />, path: '/admin/data-import', permission: 'DataImport.View' },
    { text: 'Teams', icon: <PeopleIcon />, path: null, disabled: true },
    { text: 'Departments', icon: <BusinessIcon />, path: null, disabled: true },
    { text: 'Locations', icon: <InsightsIcon />, path: null, disabled: true },
  ]);

  const platformSubItems = getVisibleNavItems([
    { text: 'Platform Dashboard', icon: <PublicIcon />, path: '/platform/dashboard', permission: 'Platform.View' },
    { text: 'Tenant Switcher', icon: <SwapHorizIcon />, path: '/platform/tenant-switcher', permission: 'Tenant.Switch' },
    // RC18.6.2 — moved from the Tenant Administration section: both are genuinely platform-wide
    // by permission (Tenant.View/FeatureManagement.View are Platform-Operator-only, confirmed in
    // the RC18.6.2A audit) and were only ever mis-filed under "Administration" by nav placement.
    { text: 'Tenants', icon: <ApartmentIcon />, path: '/admin/tenants', permission: 'Tenant.View' },
    { text: 'Feature Management', icon: <ToggleOnIcon />, path: '/admin/feature-management', permission: 'FeatureManagement.View' },
    { text: 'System Incidents', icon: <ReportProblemIcon />, path: '/platform/incidents', permission: 'Platform.View' },
    // RC18.7.1 Phase 6 — labeled "Platform System Logs" (not "System Logs") to disambiguate from
    // the tenant-scoped "System Logs" item in the Monitoring section above; a Platform Operator
    // who also holds a tenant System Logs grant would otherwise see the identical label twice.
    { text: 'Platform System Logs', icon: <ArticleOutlinedIcon />, path: '/platform/system-logs', permission: 'Platform.View' },
    { text: 'Workflow Monitor', icon: <AccountTreeIcon />, path: '/platform/workflows', permission: 'System.Operate' },
    { text: 'Backup Monitor', icon: <CloudDoneIcon />, path: '/platform/backups', permission: 'Platform.View' },
    { text: 'Production Dashboard', icon: <SpeedIcon />, path: '/platform/production-dashboard', permission: 'Platform.View' },
    { text: 'Module Health', icon: <HealthAndSafetyIcon />, path: '/platform/module-health', permission: 'Platform.View' },
    { text: 'Configuration Center', icon: <TuneIcon />, path: '/platform/configuration', permission: 'Platform.Manage' },
    { text: 'Enterprise Operations Center', icon: <SettingsSuggestIcon />, path: '/platform/enterprise-operations', permission: 'EnterpriseOperations.View' },
  ]);

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Recruiter ATS
        </Typography>
      </Toolbar>
      <List>
        {favoritePages.length > 0 && (
          <>
            <Typography variant="caption" sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Favorites
            </Typography>
            {favoritePages.map((item) => (
              <ListItem key={`fav-${item.path}`} disablePadding>
                <ListItemButton onClick={() => navigate(item.path)}>
                  <ListItemIcon sx={{ minWidth: 36 }}><StarIcon fontSize="small" color="warning" /></ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </>
        )}

        {recentPages.length > 0 && (
          <>
            <Typography variant="caption" sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Recent
            </Typography>
            {recentPages.map((item) => (
              <ListItem key={`recent-${item.path}`} disablePadding>
                <ListItemButton onClick={() => navigate(item.path)}>
                  <ListItemIcon sx={{ minWidth: 36 }}><HistoryIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </>
        )}

        {(favoritePages.length > 0 || recentPages.length > 0) && <Divider sx={{ my: 1 }} />}

        <ListItem disablePadding>
          <ListItemButton selected={location.pathname === userGuideItem.path} onClick={() => navigate(userGuideItem.path)}>
            <ListItemIcon>{userGuideItem.icon}</ListItemIcon>
            <ListItemText primary={userGuideItem.text} />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton selected={location.pathname === dashboardItem.path} onClick={() => navigate(dashboardItem.path)}>
            <ListItemIcon>{dashboardItem.icon}</ListItemIcon>
            <ListItemText primary={dashboardItem.text} />
          </ListItemButton>
        </ListItem>

        {workspaceGroups.map((group) => (
          <React.Fragment key={group.key}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => toggleGroup(group.key)}>
                <ListItemIcon>{group.icon}</ListItemIcon>
                <ListItemText primary={group.label} />
                {openGroups[group.key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={!!openGroups[group.key]} timeout="auto" unmountOnExit>
              <List disablePadding>
                {group.items.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton sx={{ pl: 4 }} selected={location.pathname === item.path} onClick={() => navigate(item.path)}>
                      <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}

        {isTenantAdmin && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setAdminOpen((o) => !o)}>
                <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                <ListItemText primary="Tenant Administration" />
                {adminOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={adminOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                {securitySubItems.length > 0 && (
                  <>
                    <ListItem disablePadding>
                      <ListItemButton sx={{ pl: 4 }} onClick={() => setSecurityOpen((o) => !o)}>
                        <ListItemIcon sx={{ minWidth: 36 }}><SecurityIcon /></ListItemIcon>
                        <ListItemText primary="Security" />
                        {securityOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={securityOpen} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {securitySubItems.map((item) => (
                          <ListItem key={item.text} disablePadding>
                            <ListItemButton sx={{ pl: 6 }} onClick={() => navigate(item.path)}>
                              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                              <ListItemText primary={item.text} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </>
                )}
                {adminSubItems.map((item) =>
                  item.disabled ? (
                    <Tooltip key={item.text} title="Coming soon" placement="right">
                      <span>
                        <ListItem disablePadding>
                          <ListItemButton disabled sx={{ pl: 4 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                          </ListItemButton>
                        </ListItem>
                      </span>
                    </Tooltip>
                  ) : (
                    <ListItem key={item.text} disablePadding>
                      <ListItemButton sx={{ pl: 4 }} onClick={() => navigate(item.path)}>
                        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    </ListItem>
                  )
                )}
              </List>
            </Collapse>
          </>
        )}

        {isPlatformOperator && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setPlatformOpen((o) => !o)}>
                <ListItemIcon><PublicIcon /></ListItemIcon>
                <ListItemText primary="Platform Administration" />
                {platformOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={platformOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                {platformSubItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => navigate(item.path)}>
                      <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <TenantDevBanner />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ display: { xs: 'none', sm: 'block' }, mr: 2 }}>
            Recruiter Management System
          </Typography>
          <GlobalSearchBar />
          <Box sx={{ flexGrow: 1 }} />
          {currentPageLabel && (
            <Tooltip title={isCurrentPageFavorite ? 'Remove from favorites' : 'Add to favorites'}>
              <IconButton
                color="inherit"
                onClick={handleToggleFavorite}
                aria-label={isCurrentPageFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                {isCurrentPageFavorite ? <StarIcon /> : <StarBorderIcon />}
              </IconButton>
            </Tooltip>
          )}
          <ThemeModeToggle />
          <NotificationBell />
        </Toolbar>
      </AppBar>
      <CommandPalette />
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </Box>

      <IdleWarningDialog
        open={idleWarningOpen}
        onStaySignedIn={handleStaySignedIn}
        onLogout={handleLogout}
      />
    </Box>
  );
};

export default Layout;
